import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["backgroundColor", "hasIcon"];
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import TipIcon from '@atlaskit/icon/core/migration/lightbulb--editor-hint';
import { PanelType } from '@atlaskit/adf-schema';
import { PanelSharedCssClassName } from '@atlaskit/editor-common/panel';
import { hexToEditorBackgroundPaletteColor } from '@atlaskit/editor-palette';
import EmojiIcon from '@atlaskit/icon/core/migration/emoji--editor-emoji';
import EmojiItem from './emoji';
import { PanelInfoIcon, PanelSuccessIcon, PanelNoteIcon, PanelWarningIcon, PanelErrorIcon } from '@atlaskit/editor-common/icons';
import { akEditorCustomIconSize } from '@atlaskit/editor-shared-styles/consts';
import { fg } from '@atlaskit/platform-feature-flags';
// New custom icons are a little smaller than predefined icons.
// To fix alignment issues with custom icons, vertical alignment is updated.
var panelEmojiSpriteVerticalAlignment = -(8 * 3 - akEditorCustomIconSize) / 2;
var panelEmojiImageVerticalAlignment = panelEmojiSpriteVerticalAlignment - 1;
var blockNodesVerticalMargin = '0.75rem';
var akEditorTableCellMinWidth = 48;
var panelBaseStyles = css({
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
  '&.ak-editor-panel': {
    borderRadius: "var(--ds-border-radius, 3px)",
    margin: "".concat(blockNodesVerticalMargin, " 0 0"),
    paddingTop: "var(--ds-space-100, 8px)",
    paddingRight: "var(--ds-space-200, 16px)",
    paddingBottom: "var(--ds-space-100, 8px)",
    paddingLeft: "var(--ds-space-100, 8px)",
    minWidth: "".concat(akEditorTableCellMinWidth, "px"),
    display: 'flex',
    position: 'relative',
    alignItems: 'normal',
    wordBreak: 'break-word',
    backgroundColor: "var(--ds-background-accent-blue-subtlest, #DEEBFF)",
    color: 'inherit',
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    '.ak-editor-panel__icon': {
      flexShrink: 0,
      height: "var(--ds-space-300, 24px)",
      width: "var(--ds-space-300, 24px)",
      boxSizing: 'content-box',
      paddingRight: "var(--ds-space-100, 8px)",
      textAlign: 'center',
      userSelect: 'none',
      MozUserSelect: 'none',
      WebkitUserSelect: 'none',
      MsUserSelect: 'none',
      // eslint-disable-next-line @atlaskit/design-system/use-tokens-space
      marginTop: '0.1em',
      color: "var(--ds-icon-information, #1D7AFC)",
      // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
      '> span': {
        verticalAlign: 'middle',
        display: 'inline-flex'
      },
      // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
      '.emoji-common-emoji-sprite': {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
        verticalAlign: "".concat(panelEmojiSpriteVerticalAlignment, "px")
      },
      // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
      '.emoji-common-emoji-image': {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
        verticalAlign: "".concat(panelEmojiImageVerticalAlignment, "px"),
        // Vertical align only works for inline-block elements in Firefox
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
        '@-moz-document url-prefix()': {
          // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
          img: {
            display: 'inline-block'
          }
        }
      }
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    '.ak-editor-panel__content': {
      margin: "var(--ds-space-025, 2px)".concat(" 0 ", "var(--ds-space-025, 2px)"),
      flex: '1 0 0',
      /*
      https://ishadeed.com/article/min-max-css/#setting-min-width-to-zero-with-flexbox
      The default value for min-width is auto, which is computed to zero. When an element is a flex item, the value of min-width doesn’t compute to zero. The minimum size of a flex item is equal to the size of its contents.
      */
      minWidth: 0
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    '&[data-panel-type="note"]': {
      backgroundColor: "var(--ds-background-accent-purple-subtlest, #EAE6FF)",
      // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
      '.ak-editor-panel__icon': {
        color: "var(--ds-icon-discovery, #8270DB)"
      }
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
    '&[data-panel-type="tip"]': {
      backgroundColor: "var(--ds-background-accent-green-subtlest, #E3FCEF)",
      // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
      '.ak-editor-panel__icon': {
        color: "var(--ds-icon-success, #22A06B)"
      }
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
    '&[data-panel-type="warning"]': {
      backgroundColor: "var(--ds-background-accent-yellow-subtlest, #FFFAE6)",
      // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
      '.ak-editor-panel__icon': {
        color: "var(--ds-icon-warning, #E56910)"
      }
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
    '&[data-panel-type="error"]': {
      backgroundColor: "var(--ds-background-accent-red-subtlest, #FFEBE6)",
      // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
      '.ak-editor-panel__icon': {
        color: "var(--ds-icon-danger, #C9372C)"
      }
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
    '&[data-panel-type="success"]': {
      backgroundColor: "var(--ds-background-accent-green-subtlest, #E3FCEF)",
      // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
      '.ak-editor-panel__icon': {
        color: "var(--ds-icon-success, #22A06B)"
      }
    }
  }
});
var panelHasNoIconStyles = css({
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
  '&.ak-editor-panel': {
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    '&[data-panel-type="custom"]': {
      paddingLeft: "var(--ds-space-150, 12px)",
      paddingRight: "var(--ds-space-150, 12px)"
    }
  }
});
var panelNestedIconStyles = css({
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
  '&.ak-editor-panel__no-icon': {
    paddingLeft: "var(--ds-space-150, 12px)",
    paddingRight: "var(--ds-space-150, 12px)"
  }
});
var nestedPanelStyles = css({
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
  '.ak-editor-panel__content .ak-editor-panel': {
    border: "1px solid ".concat("var(--ds-border, #091E42)")
  }
});
var panelCustomBackground = css({
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
  '&.ak-editor-panel': {
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    '&[data-panel-type="custom"]': {
      backgroundColor: 'var(--ak-renderer-panel-custom-bg-color)'
    }
  }
});
var PanelStyled = function PanelStyled(_ref) {
  var backgroundColor = _ref.backgroundColor,
    hasIcon = _ref.hasIcon,
    props = _objectWithoutProperties(_ref, _excluded);
  var customBackgroundColor = backgroundColor ? hexToEditorBackgroundPaletteColor(backgroundColor) || backgroundColor : undefined;
  return jsx("div", _extends({
    style:
    // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
    {
      '--ak-renderer-panel-custom-bg-color': customBackgroundColor
    },
    css: [panelBaseStyles, !hasIcon && panelHasNoIconStyles, props['data-panel-type'] === PanelType.CUSTOM && backgroundColor && panelCustomBackground, fg('platform_editor_nested_dnd_styles_changes') && panelNestedIconStyles, fg('platform_editor_add_border_for_nested_panel') && nestedPanelStyles]
    // Ignored via go/ees005
    // eslint-disable-next-line react/jsx-props-no-spreading
  }, props), props.children);
};
PanelStyled.displayName = 'PanelStyled';
var panelIcons = {
  info: PanelInfoIcon,
  success: PanelSuccessIcon,
  note: PanelNoteIcon,
  tip: TipIcon,
  warning: PanelWarningIcon,
  error: PanelErrorIcon,
  custom: EmojiIcon
};
var Panel = function Panel(props) {
  var allowCustomPanels = props.allowCustomPanels,
    type = props.panelType,
    panelColor = props.panelColor,
    panelIcon = props.panelIcon,
    panelIconId = props.panelIconId,
    panelIconText = props.panelIconText,
    providers = props.providers,
    children = props.children;
  // only allow custom panel type if flag is set
  // otherwise fall back to info if custom panel is given
  var panelType = allowCustomPanels ? type : type === PanelType.CUSTOM ? PanelType.INFO : type;
  var getIcon = function getIcon() {
    if (panelType === PanelType.CUSTOM) {
      if (panelIcon && providers) {
        return jsx(EmojiItem, {
          id: panelIconId,
          text: panelIconText,
          shortName: panelIcon,
          providers: providers
        });
      }
      return null;
    }
    var Icon = panelIcons[panelType];
    return jsx(Icon, {
      label: "".concat(panelType, " panel")
    });
  };
  var icon = getIcon();
  var renderIcon = function renderIcon() {
    if (icon) {
      // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
      return jsx("div", {
        className: PanelSharedCssClassName.icon
      }, icon);
    }
  };
  return jsx(PanelStyled
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
  , {
    className: PanelSharedCssClassName.prefix,
    "data-panel-type": panelType,
    "data-panel-color": panelColor,
    "data-panel-icon": panelIcon,
    "data-panel-icon-id": panelIconId,
    "data-panel-icon-text": panelIconText,
    backgroundColor: panelColor,
    hasIcon: Boolean(icon)
  }, renderIcon(), jsx("div", {
    className: PanelSharedCssClassName.content
  }, children));
};
export default Panel;